/**
 * Widget Dashboard « Performance commerciale par cuvée » — branché sur
 * `dashboard.performanceCuvees` (Lot H.3, cadrage métier § 2).
 *
 * GARDE-FOU MÉTIER : on parle de « performance commerciale », JAMAIS de « marge »
 * (aucune donnée de coût au MVP → une marge serait un chiffre inventé). On expose
 * uniquement du RÉEL issu des ventes : CA HT, prix moyen équivalent 75 cl, volume.
 *
 * Responsive : liste comparative sur bureau, lignes compactes sur mobile.
 * Gating : masqué sans `vente.read`.
 */

import { extractTrpcCode } from "@champ-erp/auth-primitives/web";
import type { PerformanceCuvee, PerformanceCuveesSortie } from "@champ-erp/contracts";
import { Card, Skeleton, WidgetHeader } from "@champ-erp/ui";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Wine } from "lucide-react";
import { Link } from "react-router-dom";

import { useCan } from "../../auth/use-can.js";
import { HelpBubble } from "../../components/aide/HelpBubble.js";
import { trpc } from "../../lib/trpc.js";
import { formatEurosCents } from "../ventes/shared/format-montants.js";
import { ROUTES_VENTES } from "../ventes/shared/routes.js";

const PASTILLE_STYLE: Record<PerformanceCuvee["cuvee_style"], string> = {
  blanc: "bg-champagne-300 ring-champagne-400",
  blanc_de_blancs: "bg-champagne-300 ring-champagne-400",
  blanc_de_noirs: "bg-champagne-300 ring-champagne-400",
  rose: "bg-rust-300 ring-rust-400",
};

/** Volume vendu → « 170 cols » (séparateur FR). */
function formatCols(volumeCols: number): string {
  return `${volumeCols.toLocaleString("fr-FR")} col${volumeCols > 1 ? "s" : ""}`;
}

export function WidgetPerformanceCuvee() {
  const { granted, isLoading: permLoading } = useCan("vente.read");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["dashboard.performanceCuvees"],
    queryFn: () => trpc.dashboard.performanceCuvees.query(),
    enabled: granted,
    retry: false,
  });

  if (permLoading || !granted) return null;
  if (isError && extractTrpcCode(error) === "FORBIDDEN") return null;

  return (
    <Card as="section" className="overflow-hidden p-0">
      <div className="border-b border-champagne-200 px-4 pt-4 sm:px-6">
        <WidgetHeader
          eyebrow="Commerce · Cuvées"
          title="Performance commerciale par cuvée"
          titleHelp={
            <HelpBubble
              articleSlug="comprendre-mon-tableau-de-bord"
              label="Aide : comprendre mon tableau de bord"
            />
          }
          rightSlot={
            <span className="hidden shrink-0 text-[12px] text-champagne-700 sm:inline">
              12 derniers mois
            </span>
          }
        />
      </div>
      {isLoading ? (
        <div className="p-4 sm:p-6">
          <Skeleton variant="text" lines={4} />
        </div>
      ) : isError || !data ? (
        <div className="p-4 sm:p-6">
          <p className="rounded-lg border border-dashed border-champagne-200 bg-champagne-50/40 px-4 py-6 text-center text-[13px] text-champagne-700">
            Impossible de charger la performance des cuvées pour l&apos;instant.
          </p>
        </div>
      ) : (
        <PerformanceContenu cuvees={data} />
      )}
    </Card>
  );
}

function PerformanceContenu({ cuvees }: { cuvees: PerformanceCuveesSortie }) {
  // État vide honnête : aucune vente ventilable → invitation.
  if (cuvees.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 px-4 py-6 text-center sm:px-6">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-champagne-50 text-champagne-700 ring-1 ring-champagne-200">
          <Wine size={26} />
        </div>
        <p className="text-[14px] text-champagne-800">
          Aucune vente ventilée par cuvée pour l&apos;instant.
        </p>
        <Link
          to={ROUTES_VENTES.hub}
          className="text-[13px] font-medium text-champagne-700 underline-offset-2 hover:text-champagne-900 hover:underline"
        >
          Voir les ventes →
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="hidden grid-cols-[minmax(0,1.5fr)_repeat(3,minmax(0,0.75fr))_1.25rem] gap-4 bg-champagne-50/70 px-6 py-2 text-[12px] font-medium text-champagne-800 sm:grid">
        <span>Cuvée</span>
        <span className="text-right text-aubergine-700">CA HT</span>
        <span className="text-right text-champagne-800">Prix moyen/75 cl</span>
        <span className="text-right text-vine-800">Volume vendu</span>
        <span aria-hidden="true" />
      </div>
      <div className="divide-y divide-champagne-900/10">
        {cuvees.map((cuvee) => (
          <LigneCuvee key={cuvee.cuvee_id} cuvee={cuvee} />
        ))}
      </div>
    </div>
  );
}

/** Une cuvée, ses trois indicateurs et un accès clavier direct à sa fiche. */
function LigneCuvee({ cuvee }: { cuvee: PerformanceCuvee }) {
  return (
    <Link
      to={`/cave/cuvees/${cuvee.cuvee_id}`}
      className="group block px-4 py-3.5 transition hover:bg-champagne-50/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-champagne-500 sm:grid sm:min-h-16 sm:grid-cols-[minmax(0,1.5fr)_repeat(3,minmax(0,0.75fr))_1.25rem] sm:items-center sm:gap-4 sm:px-6"
    >
      <div className="flex min-w-0 items-center gap-2.5 text-[15px] font-semibold text-aubergine-900">
        <span
          className={`h-2.5 w-2.5 shrink-0 rounded-full ring-1 ${PASTILLE_STYLE[cuvee.cuvee_style]}`}
          aria-hidden="true"
        />
        <span className="min-w-0 break-words leading-snug sm:truncate">{cuvee.cuvee_nom}</span>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-3 sm:contents">
        <div className="min-w-0 sm:text-right">
          <div className="text-[11px] font-medium text-champagne-700 sm:hidden">CA HT</div>
          <div className="mono tnum mt-0.5 text-[15px] font-semibold text-aubergine-950 sm:mt-0">
            {formatEurosCents(cuvee.ca_ht_cents)}
          </div>
        </div>
        <div className="min-w-0 sm:text-right">
          <div className="text-[11px] font-medium text-champagne-700 sm:hidden">
            Prix moy./75 cl
          </div>
          <div className="mono tnum mt-0.5 text-[14px] font-medium text-champagne-900 sm:mt-0">
            {formatEurosCents(cuvee.prix_moyen_cents)}
          </div>
        </div>
        <div className="min-w-0 sm:text-right">
          <div className="text-[11px] font-medium text-champagne-700 sm:hidden">Volume vendu</div>
          <div className="mono tnum mt-0.5 text-[14px] font-semibold text-vine-900 sm:mt-0">
            {formatCols(cuvee.volume_cols)}
          </div>
        </div>
      </div>

      <ChevronRight
        size={18}
        className="hidden shrink-0 text-champagne-500 transition-transform group-hover:translate-x-0.5 sm:block"
        aria-hidden="true"
      />
    </Link>
  );
}
